import React, { useCallback } from 'react';
import { Field, InjectedFormProps, reduxForm, SubmissionError, getFormValues } from 'redux-form';
import { toLower, set } from 'lodash';

import t from 'utils/translate';
import { passwordRegExp } from 'utils/regexp';

import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import { FORM_MODEL } from 'admin/constants/common';
import { TextField } from 'admin/components/common/Form';
import Button from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import { Column, Grid } from 'admin/components/common/Grid';
import PasswordFieldChecker from 'admin/components/common/PasswordFieldChecker';
import { isMatching, QUERY } from 'admin/components/common/Responsive';
import { updateMemberPassword } from 'admin/actions';

import css from './ChangePasswordForm.scss';

type Values = {
	oldPassword: string;
	newPassword: string;
	newPassword2: string;
};

interface OwnProps {
	onSubmit?: (values: Values) => Promise<void>;
	onCancel?: () => void;
	onSuccess?: () => void | Promise<void>;
	showCancelButton?: boolean;
	submitButtonText?: string;
	cancelButtonText?: string;
	className?: string;
}

type Props = OwnProps & InjectedFormProps<Values, OwnProps>;

const ChangePasswordForm: React.FC<Props> = ({
	handleSubmit,
	submitting,
	dirty,
	onSubmit,
	onCancel,
	onSuccess,
	showCancelButton = true,
	submitButtonText = t('profile.changePassModal.update'),
	cancelButtonText = t('profile.changePassModal.cancel'),
	className,
}) => {
	const dispatch = useAdminDispatch();
	const formData = useAdminSelector(getFormValues(FORM_MODEL.CHANGE_USER_PASSWORD)) as Values | undefined;
	const isMobile = isMatching(QUERY.MOBILE);

	const onFormSubmit = useCallback(
		async (values: Values) => {
			if (onSubmit) {
				await onSubmit(values);
			}

			const res = await dispatch(
				updateMemberPassword({
					oldPassword: values.oldPassword,
					newPassword: values.newPassword,
				})
			);

			if (res.success) {
				if (onSuccess) {
					await onSuccess();
				}
			} else if (res.errors) {
				const err = res.errors[0].message;

				if (toLower(err) === 'forbidden') {
					throw new SubmissionError({
						oldPassword: t('profile.changePassModal.submissionErrors.wrongPassword'),
					});
				}
			}
		},
		[dispatch, onSubmit, onSuccess]
	);

	const renderFields = () => (
		<Grid columns="12" columnGap={isMobile ? 'small' : 'large'}>
			<Column colSpan={isMobile ? '12' : '7'}>
				<Field
					className={css.field}
					name="oldPassword"
					component={TextField}
					autoComplete="off"
					isLabelUppercase={false}
					type="password"
					label={t('profile.changePassModal.fields.oldPassword')}
					isRequired
				/>
				<Field
					className={css.field}
					name="newPassword"
					component={TextField}
					autoComplete="off"
					isLabelUppercase={false}
					type="password"
					label={t('profile.changePassModal.fields.newPassword')}
					isRequired
				/>
				<Field
					className={css.field}
					name="newPassword2"
					component={TextField}
					autoComplete="off"
					isLabelUppercase={false}
					type="password"
					label={t('profile.changePassModal.fields.newPasswordAgain')}
					isRequired
				/>
			</Column>
			<Column alignItems="flex-end" colSpan={isMobile ? '12' : '5'}>
				<div className={css.checkPassWrap}>
					<Text className={css.checkPassTitle} weight={Text.weight.semibold}>
						{t('profile.changePassModal.checkPasswordTitle')}
					</Text>
					<PasswordFieldChecker view="vertical" value={formData?.newPassword || ''} />
				</div>
			</Column>
		</Grid>
	);

	const renderButtons = () => (
		<div className={css.buttons}>
			<Button disabled={!dirty || submitting} view="primary" type="submit" loading={submitting}>
				{submitButtonText}
			</Button>
			{showCancelButton && onCancel && (
				<Button disabled={submitting} view="secondary" onClick={onCancel}>
					{cancelButtonText}
				</Button>
			)}
		</div>
	);

	return (
		<form className={`${css.form} ${className || ''}`} onSubmit={handleSubmit(onFormSubmit)}>
			<div className={css.content}>{renderFields()}</div>
			{renderButtons()}
		</form>
	);
};

export default reduxForm<Values, OwnProps>({
	validate: (values: Values) => {
		const errors: Partial<Values> = {};

		if (!values.oldPassword) {
			set(errors, 'oldPassword', t('profile.changePassModal.errors.required'));
		}

		if (!values.newPassword) {
			set(errors, 'newPassword', t('profile.changePassModal.errors.required'));
		} else if (!passwordRegExp.test(values.newPassword)) {
			set(errors, 'newPassword', t('profile.changePassModal.errors.patternError'));
		}

		if (!values.newPassword2) {
			set(errors, 'newPassword2', t('profile.changePassModal.errors.required'));
		} else if (values.newPassword && values.newPassword2 && values.newPassword !== values.newPassword2) {
			set(errors, 'newPassword2', t('profile.changePassModal.errors.missmatchError'));
		}

		return errors;
	},
	form: FORM_MODEL.CHANGE_USER_PASSWORD,
})(ChangePasswordForm);
